/**
 * Display-currency utilities (v1.4.2 §10).
 *
 * AuthContext writes the active user's base_currency into a module-scoped store
 * after every /me or PATCH. Pages read it via `getDisplayCurrency()` so all
 * money formatting follows whatever the user picked in Settings.
 *
 * Locales chosen per ISO 4217 currency so symbols render correctly
 * (₹ for INR, £ for GBP, € for EUR, etc.).
 */

let CURRENT = 'CAD';
const SUBSCRIBERS = new Set();

const LOCALE_BY_CCY = {
  CAD: 'en-CA',
  USD: 'en-US',
  INR: 'en-IN',
  GBP: 'en-GB',
  EUR: 'en-IE',
  AUD: 'en-AU',
  SGD: 'en-SG',
  AED: 'en-AE',
  JPY: 'ja-JP',
};

export function setDisplayCurrency(ccy) {
  if (typeof ccy === 'string' && ccy.length === 3) {
    CURRENT = ccy.toUpperCase();
    SUBSCRIBERS.forEach((cb) => { try { cb(CURRENT); } catch { /* no-op */ } });
    try { window.dispatchEvent(new CustomEvent('display-currency-change', { detail: CURRENT })); } catch { /* SSR-safe */ }
  }
}

export function subscribeDisplayCurrency(cb) {
  SUBSCRIBERS.add(cb);
  return () => SUBSCRIBERS.delete(cb);
}

export function getDisplayCurrency() {
  return CURRENT;
}

export function localeFor(ccy) {
  return LOCALE_BY_CCY[ccy] || 'en-US';
}

/** Primary formatter — formats in the user's base currency unless override is given. */
export function fmtMoney(n, ccy = null, opts = {}) {
  const cur = (ccy || CURRENT || 'CAD').toUpperCase();
  const locale = localeFor(cur);
  const { minimumFractionDigits = 0, maximumFractionDigits = 0 } = opts;
  try {
    const result = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: cur,
      minimumFractionDigits,
      maximumFractionDigits,
    }).format(n || 0);
    // en-CA locale renders CAD as '$' which is ambiguous vs USD — force 'C$'
    if (cur === 'CAD') return result.replace(/^\$/, 'C$');
    return result;
  } catch {
    return `${cur} ${(n || 0).toLocaleString()}`;
  }
}
